import * as React from "react";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-experimental-images";
import { Media } from "src/typings/microserviceModels/content-flex-module";
import GalleryCarousel from "src/components/shared/Gallery/GalleryCarousel/GalleryCarousel";
import { GalleryImage } from "src/components/shared/Gallery/typings";
import { ExtendedContextStore } from "src/typings/flexFramework/FlexDefinitions";
import { ThumborStore } from "src/stores/thumbor/ThumborStore";
import { LazyLoadedImage } from "src/components/utility/LazyLoadedImage/LazyLoadedImage";
import { withStores } from "src/stores";
import LazyLoad from "react-lazyload";
import { UitkLayoutPosition } from "uitk-react-layout-position";
import { UitkCardAlohaPlaceholder } from "uitk-react-cards";

export const VideoDisplay = ({ id, video }: { id: string; video: Media }) => {
  const embedUrl = video.mediaUrl.split("watch?v=").join("embed/");

  return (
    <LazyLoad
      offset={500}
      height="100%"
      once={true}
      placeholder={<UitkCardAlohaPlaceholder data-testid="editorial-video" />}
    >
      <UitkLayoutPosition type="relative" className="contentVideoContainer" tag="div">
        <UitkLayoutPosition type="absolute" position={{ top: "zero", left: "zero" }} cloneElement={true}>
          <iframe
            id={`${id}-video`}
            title={video.mediaAlt}
            src={embedUrl}
            frameBorder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen={true}
            height="100%"
            width="100%"
          />
        </UitkLayoutPosition>
      </UitkLayoutPosition>
    </LazyLoad>
  );
};

export const ImageDisplay = withStores("thumbor")(
  ({ image, context, thumbor }: { image: Media; context: ExtendedContextStore; thumbor: ThumborStore }) => {
    const width = context?.deviceInformation?.mobile ? 360 : 1040;
    const resizedImage = thumbor.safelyGenerateThumborizedImageUrl(image.mediaUrl, width, undefined, context);

    return (
      <UitkFigure ratio={UitkFigureAspectRatioType.R16_9} className="all-image-fit-cover">
        <LazyLoadedImage src={resizedImage} alt={image.mediaAlt} />
      </UitkFigure>
    );
  }
);

export const MediaCarousel = ({ id, media, title }: { id: string; media: Media[]; title: string }) => {
  const convertedImages = media.reduce((acc: GalleryImage[], item) => {
    if (item.mediaType === "Image") {
      const { mediaUrl, mediaAlt, mediaCaption } = item;
      acc.push({
        alt: mediaAlt,
        src: mediaUrl,
        thumbnailSrc: mediaUrl,
        caption: mediaCaption,
      });
    }

    return acc;
  }, []);

  return (
    <GalleryCarousel
      analyticsId={`${id}.GalleryCarousel`}
      analyticsModule="Editorial"
      title={title}
      images={convertedImages}
      identifier={id}
    />
  );
};

interface MediaDisplayProps {
  id: string;
  media: Media[];
  context: ExtendedContextStore;
  title?: string;
}

export const MediaDisplay = ({ id, media, context, title }: MediaDisplayProps) => {
  if (media.length === 1) {
    const mediaItem = media[0];
    if (mediaItem.mediaType === "Image") {
      return <ImageDisplay image={mediaItem} context={context} />;
    }

    return <VideoDisplay id={id} video={mediaItem} />;
  }

  return <MediaCarousel id={id} media={media} title={title || ""} />;
};

export default MediaDisplay;
